/**
 * Smith Protective — Prospects Table (Iter160).
 *
 * Filter, sort and open the security-industry prospect universe.
 * Master-only tenant module.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Shield, Search, Filter, Loader2, ArrowRight, RefreshCw, Building2,
  DollarSign, Users, MapPin, X, Plus, ChevronLeft, ChevronRight,
  Bookmark, BookmarkPlus, Trash2, Wand2, CheckSquare, Square, Upload,
  ChevronDown, ChevronUp, Mail, Zap, Rocket, Archive,
} from "lucide-react";
import Sidebar from "../../components/Sidebar";
import AppHeader from "../../components/SmithAppHeader";
import PlacesAutocomplete from "../../components/PlacesAutocomplete";
import SmithImportExport from "../../components/SmithImportExport";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = "#0B1120";
const usd = (n) => (n ? `$${Math.round(n).toLocaleString("en-US")}` : "—");
const PAGE_SIZE = 30;                                // Iter165 — per user spec

const TIER_COLOR = {
  hot: "#DC2626", high: "#16A34A", qualified: "#EAB308",
  nurture: "#3B82F6", low: "#94A3B8",
};

// Iter234 · Tier ordering + metadata for the accordion grouping. Layers
// are rendered top-to-bottom in this order so the hottest leads sit at
// the top of the cockpit.
const TIER_META = [
  { id: "hot",       label: "Hot",       icon: "🔥", color: "#DC2626" },
  { id: "high",      label: "High",      icon: "🟢", color: "#16A34A" },
  { id: "qualified", label: "Qualified", icon: "🟡", color: "#EAB308" },
  { id: "nurture",   label: "Nurture",   icon: "🔵", color: "#3B82F6" },
  { id: "low",       label: "Low",       icon: "⚪", color: "#94A3B8" },
];

const _hasValidEmail = (p) => {
  const dms = Array.isArray(p?.decision_makers) ? p.decision_makers : [];
  return dms.some((d) => typeof d?.email === "string" && d.email.includes("@"));
};

export default function SmithProspects() {
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);   // Iter256 · std header
  // Iter265 · mobile filter drawer toggle (default collapsed on <md)
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [stateStats, setStateStats] = useState([]);
  const [segmentStats, setSegmentStats] = useState([]);

  const [q, setQ] = useState(sp.get("q") || "");
  const [segments, setSegments] = useState([]);
  // Multi-select chips (Iter165). URL param is CSV.
  const initialSegs = (sp.get("segments") || sp.get("segment") || "")
    .split(",").map((s) => s.trim()).filter(Boolean);
  const [selectedSegments, setSelectedSegments] = useState(initialSegs);
  const [addingSegment, setAddingSegment] = useState(false);
  const [city, setCity] = useState(sp.get("city") || "");
  const [stateFilter, setStateFilter] = useState(sp.get("state") || "");
  const [tier, setTier] = useState(sp.get("tier") || "");
  const [minScore, setMinScore] = useState(Number(sp.get("min_score")) || 0);
  const [minSqft, setMinSqft] = useState(Number(sp.get("min_sqft")) || 0);
  const [maxSqft, setMaxSqft] = useState(Number(sp.get("max_sqft")) || 0);
  // Iter178 — Smart Trigger Search
  const [triggerTypes, setTriggerTypes] = useState([]);
  const initialTrig = (sp.get("trigger_types") || "")
    .split(",").map((s) => s.trim()).filter(Boolean);
  const [selectedTriggers, setSelectedTriggers] = useState(initialTrig);
  const [triggerKw, setTriggerKw] = useState(sp.get("trigger_kw") || "");
  // Iter178 — Radius / Metropolitan Geo-Search
  const [radiusCity, setRadiusCity] = useState(sp.get("radius_city") || "");
  const [radiusLat, setRadiusLat] = useState(Number(sp.get("center_lat")) || 0);
  const [radiusLng, setRadiusLng] = useState(Number(sp.get("center_lng")) || 0);
  const [radiusMiles, setRadiusMiles] = useState(Number(sp.get("radius_miles")) || 0);
  // Iter183 — Provider filter (Iter184 — now multi-select CSV)
  const initialProviders = (sp.get("provider") || "")
    .split(",").map((s) => s.trim()).filter(Boolean);
  const [selectedProviders, setSelectedProviders] = useState(initialProviders);
  const provider = selectedProviders.join(",");
  const [providerStats, setProviderStats] = useState([]);
  // Iter186 — Source & Batch filters
  const [source, setSource] = useState(sp.get("source") || "");
  const [batch, setBatch] = useState(sp.get("batch") || "");
  const [batchInfo, setBatchInfo] = useState(null);
  // Iter178 — Smart Fallback state (server tells us WHICH filters to relax)
  const [fallbackSuggestions, setFallbackSuggestions] = useState([]);
  // Iter178 — Saved Views (named filter combos)
  const [savedViews, setSavedViews] = useState([]);
  const [savingView, setSavingView] = useState(false);
  // Iter181 — Bulk Enrich state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkJob, setBulkJob] = useState(null);              // {job_id, total, progress, ok_count, dms_total, status}
  const [bulkRunning, setBulkRunning] = useState(false);
  const [sort, setSort] = useState(sp.get("sort") || "opportunity_score");
  const [page, setPage] = useState(Number(sp.get("page")) || 1);

  // Iter234 · Hygiene toggle — hide leads without a valid DM email.
  const [onlyWithEmail, setOnlyWithEmail] = useState(() => {
    try { return localStorage.getItem("smith.prospects.onlyWithEmail") !== "0"; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("smith.prospects.onlyWithEmail", onlyWithEmail ? "1" : "0"); }
    catch { /* quota */ }
  }, [onlyWithEmail]);

  // Iter234 · Tier accordion — all tiers collapsed by default; user
  // decides what to open. State persisted per-tier in localStorage.
  const [expandedTiers, setExpandedTiers] = useState(() => {
    try {
      const raw = localStorage.getItem("smith.prospects.expandedTiers");
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  useEffect(() => {
    try {
      localStorage.setItem("smith.prospects.expandedTiers",
        JSON.stringify(Array.from(expandedTiers)));
    } catch { /* quota */ }
  }, [expandedTiers]);
  const toggleTierExpand = (id) => setExpandedTiers((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const expandAllTiers = () => setExpandedTiers(new Set(TIER_META.map((t) => t.id)));
  const collapseAllTiers = () => setExpandedTiers(new Set());

  // Iter236 · Per-tier segment/city filters — reps drill into "Hot in
  // Miami" without opening every group. `tierFilters` shape:
  //   { hot: { segment: "warehouses", city: "Miami" }, ... }
  const [tierFilters, setTierFilters] = useState(() => {
    try {
      const raw = localStorage.getItem("smith.prospects.tierFilters");
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  useEffect(() => {
    try {
      localStorage.setItem("smith.prospects.tierFilters",
        JSON.stringify(tierFilters));
    } catch { /* quota */ }
  }, [tierFilters]);
  const setTierFilter = (tierId, key, value) => setTierFilters((cur) => {
    const t = { ...(cur[tierId] || {}) };
    if (t[key] === value) delete t[key];   // toggle off when clicking same chip
    else t[key] = value;
    const next = { ...cur, [tierId]: t };
    // Auto-expand the tier when a filter is engaged so the operator sees
    // what changed without a second click.
    if (t[key]) setExpandedTiers((cur2) => new Set([...cur2, tierId]));
    return next;
  });
  const clearTierFilters = (tierId) => setTierFilters((cur) => {
    const next = { ...cur };
    delete next[tierId];
    return next;
  });

  // Iter234 · Sweep-all state (background async + paginated sync).
  const [sweepJob, setSweepJob] = useState(null);       // {job_id, total, processed, phase, recovered, unresolvable, archived}
  const [sweepStarting, setSweepStarting] = useState(false);
  const [pagedSweepRunning, setPagedSweepRunning] = useState(false);
  const [pagedSweepStats, setPagedSweepStats] = useState(null); // {processed, recovered, unresolvable, batches, total}

  // Keep URL params in sync
  useEffect(() => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (selectedSegments.length) next.set("segments", selectedSegments.join(","));
    if (city) next.set("city", city);
    if (stateFilter) next.set("state", stateFilter);
    if (tier) next.set("tier", tier);
    if (minScore) next.set("min_score", String(minScore));
    if (minSqft) next.set("min_sqft", String(minSqft));
    if (maxSqft) next.set("max_sqft", String(maxSqft));
    if (selectedTriggers.length) next.set("trigger_types", selectedTriggers.join(","));
    if (triggerKw) next.set("trigger_kw", triggerKw);
    if (radiusMiles && radiusLat && radiusLng) {
      next.set("radius_miles", String(radiusMiles));
      next.set("center_lat", String(radiusLat));
      next.set("center_lng", String(radiusLng));
      if (radiusCity) next.set("radius_city", radiusCity);
    }
    if (provider) next.set("provider", provider);
    if (source) next.set("source", source);
    if (batch) next.set("batch", batch);
    if (sort && sort !== "opportunity_score") next.set("sort", sort);
    if (page > 1) next.set("page", String(page));
    setSp(next, { replace: true });
  }, [q, selectedSegments, city, stateFilter, tier, minScore, minSqft, maxSqft,
      selectedTriggers, triggerKw, radiusCity, radiusLat, radiusLng, radiusMiles,
      provider, source, batch, sort, page, setSp]);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (selectedSegments.length) p.set("segments", selectedSegments.join(","));
    if (city) p.set("city", city);
    if (stateFilter) p.set("state", stateFilter);
    if (tier) p.set("tier", tier);
    if (minScore) p.set("min_score", String(minScore));
    if (minSqft) p.set("min_sqft", String(minSqft));
    if (maxSqft) p.set("max_sqft", String(maxSqft));
    if (selectedTriggers.length) p.set("trigger_types", selectedTriggers.join(","));
    if (triggerKw) p.set("trigger_kw", triggerKw);
    if (radiusMiles && radiusLat && radiusLng) {
      p.set("radius_miles", String(radiusMiles));
      p.set("center_lat", String(radiusLat));
      p.set("center_lng", String(radiusLng));
    }
    if (provider) p.set("provider", provider);
    if (source) p.set("source", source);
    if (batch) p.set("batch", batch);
    p.set("sort", sort);
    p.set("page", String(page));
    p.set("page_size", String(PAGE_SIZE));
    return p.toString();
  }, [q, selectedSegments, city, stateFilter, tier, minScore, minSqft, maxSqft,
      selectedTriggers, triggerKw, radiusLat, radiusLng, radiusMiles,
      provider, source, batch, sort, page]);

  // Reset to page 1 whenever a filter (not page itself) changes
  useEffect(() => { setPage(1); }, [q, selectedSegments, city, stateFilter, tier, minScore, minSqft, maxSqft, selectedTriggers, triggerKw, radiusLat, radiusLng, radiusMiles, provider, source, batch, sort]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/smith/prospects?${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setFallbackSuggestions(data.fallback_suggestions || []);
      setLastUpdated(new Date());               // Iter256 · std header
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar prospects");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [params]);

  // Iter234 · Filter+group items by tier for the accordion view.
  const visibleItems = useMemo(() => {
    if (!onlyWithEmail) return items;
    return items.filter(_hasValidEmail);
  }, [items, onlyWithEmail]);
  const hiddenNoEmailCount = items.length - visibleItems.length;

  const tierGroups = useMemo(() => {
    const buckets = new Map(TIER_META.map((t) => [t.id, []]));
    for (const p of visibleItems) {
      const tid = p?.tier?.tier || "low";
      const arr = buckets.get(tid) || buckets.get("low");
      arr.push(p);
    }
    return TIER_META.map((t) => {
      const allRows = buckets.get(t.id) || [];
      // Compute top 3 segments + top 3 cities from the UNFILTERED tier
      // pool so the chips stay stable while the operator drills in.
      const segCounts = new Map();
      const cityCounts = new Map();
      for (const p of allRows) {
        const seg = (p.segment || "").trim();
        if (seg) segCounts.set(seg, (segCounts.get(seg) || 0) + 1);
        const city = (p.property?.city || "").trim();
        if (city) cityCounts.set(city, (cityCounts.get(city) || 0) + 1);
      }
      const top = (m, n) => [...m.entries()]
        .sort((a, b) => b[1] - a[1]).slice(0, n)
        .map(([value, count]) => ({ value, count }));
      const topSegments = top(segCounts, 4);
      const topCities   = top(cityCounts, 4);

      // Apply per-tier filters (segment + city).
      const f = tierFilters[t.id] || {};
      const rows = allRows.filter((p) => {
        if (f.segment && (p.segment || "") !== f.segment) return false;
        if (f.city && (p.property?.city || "") !== f.city) return false;
        return true;
      });
      const acv = rows.reduce((s, p) => s + (p?.opportunity?.acv_est || 0), 0);
      const totalAcv = allRows.reduce((s, p) => s + (p?.opportunity?.acv_est || 0), 0);
      const noEmail = rows.filter((p) => !_hasValidEmail(p)).length;
      const filterActive = !!(f.segment || f.city);
      return {
        ...t,
        rows,
        count: rows.length,
        total_count: allRows.length,
        acv,
        total_acv: totalAcv,
        no_email: noEmail,
        top_segments: topSegments,
        top_cities:   topCities,
        filter: f,
        filter_active: filterActive,
      };
    });
  }, [visibleItems, tierFilters]);

  // Iter234 · Sweep-all polling (background async job).
  useEffect(() => {
    if (!sweepJob?.job_id || sweepJob.phase === "done") return undefined;
    const tick = async () => {
      try {
        const { data } = await axios.get(
          `${API}/smith/find-email/sweep-status/${sweepJob.job_id}`);
        setSweepJob((cur) => ({ ...(cur || {}), ...data }));
        if (data.phase === "done") {
          toast.success(
            `Sweep concluído · ${data.recovered} recuperados · ${data.archived} arquivados`,
            { duration: 8000 });
          load();
        }
      } catch { /* transient — will retry */ }
    };
    const i = setInterval(tick, 4000);
    tick();
    return () => clearInterval(i);
  }, [sweepJob?.job_id, sweepJob?.phase]);

  // Load latest sweep on mount so a page refresh reveals in-progress work.
  useEffect(() => {
    axios.get(`${API}/smith/find-email/sweep-latest`).then(({ data }) => {
      if (data && data.job_id) setSweepJob(data);
    }).catch(() => {});
  }, []);

  const startSweepAll = async ({ auto_archive = true } = {}) => {
    if (!confirm("Rodar sweep em background em TODOS os prospects sem e-mail? Pode levar 10-30 min.")) return;
    setSweepStarting(true);
    try {
      const { data } = await axios.post(
        `${API}/smith/find-email/sweep-all`,
        { auto_archive, include_repository: false });
      if (!data.job_id) {
        toast.info(data.message || "Nada a processar");
        return;
      }
      if (data.already_running) {
        toast.info("Já existe um sweep rodando — mostrando progresso");
      } else {
        toast.success(`Sweep iniciado em ${data.total_targets} prospects`);
      }
      setSweepJob({ job_id: data.job_id, total: data.total_targets,
                     processed: 0, phase: "running" });
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha ao iniciar sweep");
    } finally { setSweepStarting(false); }
  };

  const runPagedSweep = async () => {
    // Synchronous 25-per-batch loop with UI progress. Bounded by total.
    if (!confirm("Rodar sweep paginado de 25 em 25? Você verá o progresso na tela.")) return;
    setPagedSweepRunning(true);
    setPagedSweepStats({ processed: 0, recovered: 0, unresolvable: 0, batches: 0, total: 0 });
    let batches = 0;
    let acc = { processed: 0, recovered: 0, unresolvable: 0, batches: 0, total: 0 };
    try {
      // Loop until a batch reports 0 targets (nothing left to enrich).
      // Hard cap at 20 batches (500 prospects) to prevent runaway.
      while (batches < 20) {
        const { data } = await axios.post(
          `${API}/smith/find-email/bulk`, { limit: 25 },
          { timeout: 300000 });
        const t = data?.targets || 0;
        if (t === 0) break;
        acc = {
          processed: acc.processed + t,
          recovered: acc.recovered + (data.total_new_emails ? 1 : 0) * 0
                       + ((data.results || []).filter((r) => r.new_count > 0).length),
          unresolvable: acc.unresolvable + (data.unresolvable || 0),
          batches: batches + 1,
          total: acc.total + t,
        };
        setPagedSweepStats(acc);
        batches += 1;
        // If we processed fewer than the requested limit, we're done.
        if (t < 25) break;
      }
      toast.success(
        `Sweep paginado concluído · ${acc.recovered} recuperados em ${acc.batches} batches`,
        { duration: 8000 });
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Sweep interrompido");
    } finally { setPagedSweepRunning(false); }
  };
  useEffect(() => {
    axios.get(`${API}/smith/segments`).then(({ data }) => setSegments(data.segments || [])).catch(() => {});
    axios.get(`${API}/smith/prospects/stats/by-state`)
      .then(({ data }) => setStateStats(data.states || [])).catch(() => {});
    axios.get(`${API}/smith/prospects/stats/by-segment`)
      .then(({ data }) => setSegmentStats(data.segments || [])).catch(() => {});
    axios.get(`${API}/smith/trigger-types`)
      .then(({ data }) => setTriggerTypes(data.trigger_types || [])).catch(() => {});
    axios.get(`${API}/smith/saved-views`)
      .then(({ data }) => setSavedViews(data.items || [])).catch(() => {});
  }, []);
  // Iter186 — Batch info when arriving from /smith/imports?batch=<id>
  useEffect(() => {
    if (!batch) { setBatchInfo(null); return; }
    axios.get(`${API}/smith/import-batches/${batch}`)
      .then(({ data }) => setBatchInfo(data)).catch(() => setBatchInfo(null));
  }, [batch]);
  // Iter182 — Cities dropdown (scoped to selected state when present)
  const [cities, setCities] = useState([]);
  useEffect(() => {
    const params = new URLSearchParams();
    if (stateFilter) params.set("state", stateFilter);
    params.set("limit", "200");
    axios.get(`${API}/smith/prospects/stats/by-city?${params}`)
      .then(({ data }) => setCities(data.cities || [])).catch(() => setCities([]));
    // Iter183 — providers scoped to selected state too
    const pp = new URLSearchParams();
    if (stateFilter) pp.set("state", stateFilter);
    axios.get(`${API}/smith/prospects/stats/by-provider?${pp}`)
      .then(({ data }) => setProviderStats(data.providers || [])).catch(() => setProviderStats([]));
  }, [stateFilter]);

  const activeStateBbox = stateFilter
    ? stateStats.find((s) => s.state === stateFilter)?.bbox
    : null;

  const clearFilters = () => {
    setQ(""); setSelectedSegments([]); setCity(""); setStateFilter(""); setTier("");
    setMinScore(0); setMinSqft(0); setMaxSqft(0);
    setSelectedTriggers([]); setTriggerKw("");
    setRadiusCity(""); setRadiusLat(0); setRadiusLng(0); setRadiusMiles(0);
    setSelectedProviders([]);
    setSource(""); setBatch("");
  };
  const hasFilter = !!(q || selectedSegments.length || city || stateFilter || tier
    || minScore || minSqft || maxSqft
    || selectedTriggers.length || triggerKw
    || (radiusMiles && radiusLat && radiusLng)
    || provider || source || batch);
  const availableSegments = segments.filter((s) => !selectedSegments.includes(s.id));
  const toggleSegment = (id) => {
    setSelectedSegments((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
    setAddingSegment(false);
  };
  const removeSegment = (id) => setSelectedSegments((cur) => cur.filter((x) => x !== id));
  const segmentName = (id) => segments.find((s) => s.id === id)?.name || id;

  // Iter178 — Smart Trigger toggle
  const toggleTrigger = (t) =>
    setSelectedTriggers((cur) => cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]);

  // Iter178 — Apply a fallback suggestion (relax = filter key OR "all"|"radius")
  const applyFallback = (relax) => {
    if (relax === "all") { clearFilters(); return; }
    if (relax === "radius") { setRadiusCity(""); setRadiusLat(0); setRadiusLng(0); setRadiusMiles(0); return; }
    const map = {
      q: () => setQ(""),
      tier: () => setTier(""),
      city: () => setCity(""),
      state: () => setStateFilter(""),
      segments: () => setSelectedSegments([]),
      segment: () => setSelectedSegments([]),
      min_score: () => setMinScore(0),
      min_sqft: () => setMinSqft(0),
      max_sqft: () => setMaxSqft(0),
      min_employees: () => {}, // not in state, no-op
      is_24_7: () => {},
      status: () => {},
      provider_status: () => {},
      trigger_types: () => setSelectedTriggers([]),
      trigger_kw: () => setTriggerKw(""),
      provider: () => setSelectedProviders([]),
    };
    (map[relax] || (() => {}))();
  };

  // Format a raw trigger type (`security_hiring` → `Security Hiring`).
  const triggerLabel = (t) => (t || "").split("_").map(
    (s) => s ? s[0].toUpperCase() + s.slice(1) : s
  ).join(" ");

  // Iter178 — Saved Views handlers
  const currentFilters = () => ({
    q, city, state: stateFilter, tier,
    min_score: minScore, min_sqft: minSqft, max_sqft: maxSqft,
    segments: selectedSegments.join(","),
    trigger_types: selectedTriggers.join(","),
    trigger_kw: triggerKw,
    provider,
    ...(radiusMiles && radiusLat && radiusLng ? {
      radius_miles: radiusMiles, center_lat: radiusLat,
      center_lng: radiusLng, radius_city: radiusCity,
    } : {}),
    sort: sort !== "opportunity_score" ? sort : undefined,
  });

  const saveCurrentView = async () => {
    if (!hasFilter) {
      toast.error("Adicione ao menos um filtro antes de salvar.");
      return;
    }
    const name = window.prompt("Nome dessa busca (ex: 'Miami 49xx Cold Storage')");
    if (!name || !name.trim()) return;
    setSavingView(true);
    try {
      const { data } = await axios.post(`${API}/smith/saved-views`,
        { name: name.trim(), filters: currentFilters() });
      setSavedViews((cur) => [data, ...cur]);
      toast.success(`Busca "${data.name}" salva`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar busca");
    } finally { setSavingView(false); }
  };

  const applySavedView = (view) => {
    const f = view.filters || {};
    setQ(f.q || "");
    setSelectedSegments(
      f.segments ? String(f.segments).split(",").filter(Boolean)
                 : (f.segment ? [f.segment] : [])
    );
    setCity(f.city || "");
    setStateFilter(f.state || "");
    setTier(f.tier || "");
    setMinScore(Number(f.min_score) || 0);
    setMinSqft(Number(f.min_sqft) || 0);
    setMaxSqft(Number(f.max_sqft) || 0);
    setSelectedTriggers(
      f.trigger_types ? String(f.trigger_types).split(",").filter(Boolean) : []
    );
    setTriggerKw(f.trigger_kw || "");
    setRadiusLat(Number(f.center_lat) || 0);
    setRadiusLng(Number(f.center_lng) || 0);
    setRadiusMiles(Number(f.radius_miles) || 0);
    setRadiusCity(f.radius_city || "");
    setSelectedProviders((f.provider || "").split(",").map((x) => x.trim()).filter(Boolean));
    setSort(f.sort || "opportunity_score");
    toast.success(`Busca "${view.name}" aplicada`);
  };

  const deleteSavedView = async (view, e) => {
    e.stopPropagation();
    if (!window.confirm(`Excluir busca "${view.name}"?`)) return;
    try {
      await axios.delete(`${API}/smith/saved-views/${view.id}`);
      setSavedViews((cur) => cur.filter((v) => v.id !== view.id));
      toast.success("Busca excluída");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao excluir");
    }
  };

  // Iter181 — Bulk Enrich handlers
  const toggleRowSelection = (pid, e) => {
    e.stopPropagation();
    setSelectedIds((cur) => {
      const next = new Set(cur);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds((cur) => {
      const allShown = items.map((p) => p.id);
      const allSelected = allShown.every((id) => cur.has(id));
      if (allSelected) return new Set();
      return new Set(allShown);
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const pollBulkJob = async (jobId) => {
    // Long-poll every 1.5s until status === "done"
    let tries = 0;
    while (tries < 200) {
      try {
        const { data } = await axios.get(`${API}/smith/prospects/enrich-jobs/${jobId}`);
        setBulkJob(data);
        if (data.status === "done") {
          setBulkRunning(false);
          toast.success(`Bulk enrich completo: ${data.ok_count}/${data.total} OK · +${data.dms_total} DMs`);
          await load();
          return;
        }
      } catch { /* ignore transient */ }
      await new Promise((r) => setTimeout(r, 1500));
      tries += 1;
    }
    setBulkRunning(false);
  };

  const runBulkEnrich = async () => {
    if (selectedIds.size === 0) {
      toast.error("Selecione ao menos 1 prospect");
      return;
    }
    if (selectedIds.size > 50) {
      toast.error("Máximo 50 prospects por lote");
      return;
    }
    if (!window.confirm(`Rodar enrich em ${selectedIds.size} prospect(s)? Isso vai levar ~${Math.ceil(selectedIds.size * 3)}s em background.`)) return;
    setBulkRunning(true);
    setBulkJob(null);
    try {
      const { data } = await axios.post(`${API}/smith/prospects/enrich-bulk`,
        { prospect_ids: Array.from(selectedIds), limit: selectedIds.size });
      toast.info(`Bulk enrich iniciado · ${data.total} prospect(s)`);
      setBulkJob({ ...data, progress: 0 });
      pollBulkJob(data.job_id);
      setSelectedIds(new Set());
    } catch (e) {
      setBulkRunning(false);
      toast.error(e?.response?.data?.detail || "Falha ao iniciar bulk enrich");
    }
  };

  return (
    <div className="min-h-screen" style={{ background: NAVY, color: "#E2E8F0" }}>
      <Sidebar />
      <div className="lg:ml-[var(--al-sidebar-w,260px)]">
        <AppHeader
          testid="smith-prospects-header"
          claireQuestion="Como uso o Smith · Prospects — filtros de score, ICP fit, trigger events e ações em lote?"
          lastUpdated={lastUpdated}
          onRefresh={load}
          refreshing={loading}
          title={
            <span className="inline-flex items-center gap-2">
              <span>Smith · Prospects</span>
              <span
                data-testid="smith-prospects-count-badge"
                title={`${total.toLocaleString("en-US")} accounts disponíveis`}
                className="text-[11px] font-black px-2 py-0.5 rounded-full text-white tabular-nums"
                style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
                {total.toLocaleString("en-US")}
              </span>
            </span>
          }
          subtitle={`${total} accounts on file · scored by ICP fit + trigger events`}
          logo={
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                 style={{ background: "linear-gradient(135deg,#1E3A8A 0%,#0F172A 100%)", color: "#DC2626" }}>
              <Shield className="w-5 h-5" />
            </div>
          }
        >
          <Link to="/smith" data-testid="smith-dashboard-nav"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold text-white hover:border-[#DC2626]">
            Dashboard
          </Link>
          <Link to="/smith/icps" data-testid="smith-icps-nav"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold text-white hover:border-[#DC2626]">
            ICPs
          </Link>
          <Link to="/smith/map" data-testid="smith-map-nav"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold text-white hover:border-[#DC2626]">
            Territory Map
          </Link>
          <button type="button" onClick={load} disabled={loading}
            data-testid="smith-refresh-btn"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold hover:border-[#DC2626] inline-flex items-center gap-1.5">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          {/* Iter234 · Sweep-all buttons */}
          <button type="button" onClick={() => startSweepAll({ auto_archive: true })}
            disabled={sweepStarting || (sweepJob?.phase === "running")}
            data-testid="prospects-sweep-bg-btn"
            title="Roda enrich em background em todos os leads sem e-mail (30 min)"
            className="h-9 px-3 rounded-lg text-[12.5px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#0EA5E9 0%,#0369A1 100%)" }}>
            {sweepStarting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}
            Enriquecer todos (BG)
          </button>
          <button type="button" onClick={runPagedSweep}
            disabled={pagedSweepRunning || (sweepJob?.phase === "running")}
            data-testid="prospects-sweep-paged-btn"
            title="Roda em batches de 25 com barra de progresso na UI"
            className="h-9 px-3 rounded-lg text-[12.5px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#8B5CF6 0%,#6D28D9 100%)" }}>
            {pagedSweepRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
            Sweep paginado
          </button>
          {/* Iter235 · Repository Recovery */}
          <Link to="/smith/repository"
            data-testid="prospects-repository-link"
            title="Ver leads arquivados por falha de enrichment"
            className="h-9 px-3 rounded-lg border border-amber-500/40 text-[12.5px] font-bold text-amber-200 hover:border-amber-400 hover:bg-amber-500/10 inline-flex items-center gap-1.5">
            <Archive className="w-3.5 h-3.5" /> Repositório
          </Link>
          <SmithImportExport
            filters={{
              q, segments: selectedSegments, city, state: stateFilter, tier,
              min_score: minScore, min_sqft: minSqft, max_sqft: maxSqft,
              trigger_types: selectedTriggers.join(","),
              trigger_kw: triggerKw,
              provider,
              ...(radiusMiles && radiusLat && radiusLng ? {
                radius_miles: radiusMiles,
                center_lat: radiusLat,
                center_lng: radiusLng,
              } : {}),
            }}
            onImported={load}
            testidPrefix="smith-prospects-io"
          />
        </AppHeader>

        {/* Iter249 · View toggle — Prospects (table) ↔ Kanban.
            Both routes stay independent full pages; this toggle lets
            the operator flip views without hunting the sidebar. */}
        <div className="max-w-[1400px] mx-auto px-3 sm:px-6 lg:px-8 pt-3">
          <ProspectsViewToggle current="table" />
        </div>

        <main className="px-3 sm:px-6 lg:px-8 py-4 sm:py-6 max-w-[1400px] mx-auto space-y-4 pb-24 lg:pb-6"
              data-testid="smith-prospects-page">
          {/* State Rollup Cards — Iter166 */}
          {stateStats.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1" data-testid="state-rollups">
              <button type="button"
                onClick={() => setStateFilter("")}
                data-testid="state-rollup-all"
                className={"shrink-0 rounded-xl border p-3 min-w-[110px] text-left transition-all "
                           + (!stateFilter
                              ? "border-[#DC2626] bg-[#DC2626]/15"
                              : "border-white/10 bg-black/20 hover:border-white/30")}>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">All states</div>
                <div className="text-[20px] font-extrabold text-white tabular-nums">
                  {stateStats.reduce((s, r) => s + r.count, 0)}
                </div>
                <div className="text-[10.5px] text-slate-500">accounts</div>
              </button>
              {stateStats.map((s) => (
                <button key={s.state} type="button"
                  onClick={() => setStateFilter(s.state === stateFilter ? "" : s.state)}
                  data-testid={`state-rollup-${s.state}`}
                  className={"shrink-0 rounded-xl border p-3 min-w-[130px] text-left transition-all "
                             + (stateFilter === s.state
                                ? "border-[#DC2626] bg-[#DC2626]/15"
                                : "border-white/10 bg-black/20 hover:border-white/30")}>
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{s.state}</div>
                    {s.hot > 0 && (
                      <span className="text-[9.5px] font-bold px-1 py-0.5 rounded bg-red-500/20 text-red-300">
                        {s.hot} hot
                      </span>
                    )}
                  </div>
                  <div className="text-[20px] font-extrabold text-white tabular-nums">{s.count}</div>
                  <div className="text-[10.5px] text-emerald-400 font-bold tabular-nums">
                    ${(s.acv_sum / 1_000_000).toFixed(1)}M
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Segment Rollup Cards — Iter167 */}
          {segmentStats.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1" data-testid="segment-rollups">
              {segmentStats.map((sg) => {
                const active = selectedSegments.includes(sg.segment);
                return (
                  <button key={sg.segment} type="button"
                    onClick={() => {
                      setSelectedSegments((cur) => cur.includes(sg.segment)
                        ? cur.filter((x) => x !== sg.segment)
                        : [...cur, sg.segment]);
                    }}
                    data-testid={`segment-rollup-${sg.segment}`}
                    className={"shrink-0 rounded-xl border p-2.5 min-w-[130px] text-left transition-all "
                               + (active
                                  ? "border-[#EAB308] bg-[#EAB308]/15"
                                  : "border-white/10 bg-black/20 hover:border-white/30")}>
                    <div className="flex items-center justify-between">
                      <div className="text-[9.5px] uppercase tracking-widest text-slate-400 font-bold truncate">
                        {sg.label}
                      </div>
                      {sg.on_icp > 0 && (
                        <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-emerald-500/20 text-emerald-300 shrink-0">
                          {sg.on_icp} ICP
                        </span>
                      )}
                    </div>
                    <div className="text-[17px] font-extrabold text-white tabular-nums">{sg.count}</div>
                    <div className="text-[10px] text-emerald-400 font-bold tabular-nums">
                      ${(sg.acv_sum / 1_000_000).toFixed(1)}M
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Filter bar — Iter165 · Iter265 mobile collapse */}
          <div className="rounded-2xl border p-3 sm:p-4 space-y-3"
               style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(220,38,38,0.20)" }}>

            {/* Iter265 · Mobile toggle bar — hidden on md+ */}
            <button type="button"
                    onClick={() => setMobileFiltersOpen((v) => !v)}
                    data-testid="mobile-filters-toggle"
                    className="md:hidden w-full h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-[13px] font-bold text-slate-100 inline-flex items-center justify-between gap-2 touch-target">
              <span className="inline-flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filtros {(q || stateFilter || city || provider || source || batch) && <span className="w-2 h-2 rounded-full bg-[#DC2626]" />}
              </span>
              <span className={"text-slate-400 transition-transform " + (mobileFiltersOpen ? "rotate-180" : "")}>▼</span>
            </button>

            {/* Iter265 · mobile-collapsible wrapper — all filter rows below
                are hidden on <md unless the operator opens the drawer */}
            <div className={"space-y-3 " + (mobileFiltersOpen ? "" : "hidden md:block")}
                 data-testid="mobile-filters-panel">
            {/* Iter186 — Batch context banner */}
            {batchInfo && (              <div className="flex items-center justify-between gap-2 flex-wrap p-2 rounded-lg border border-indigo-400/40 bg-indigo-500/10"
                   data-testid="batch-banner">
                <div className="text-[12px] text-indigo-100">
                  <strong className="text-white">Import batch:</strong> {batchInfo.filename}
                  <span className="text-slate-400 ml-2">
                    · {batchInfo.inserted || 0} novos + {batchInfo.updated || 0} atualizados · {batchInfo.uploaded_by}
                  </span>
                </div>
                <button type="button" onClick={() => setBatch("")}
                  data-testid="batch-clear"
                  className="h-7 px-2 rounded border border-white/15 text-[10.5px] font-bold text-slate-300 hover:border-[#DC2626] inline-flex items-center gap-1">
                  <X className="w-3 h-3" /> Ver todos
                </button>
              </div>
            )}
            {/* Iter186 — Source quick-filter */}
            {!batch && (
              <div className="flex flex-wrap items-center gap-2"
                   data-testid="source-filter-row">
                <span className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold inline-flex items-center gap-1">
                  <Upload className="w-3.5 h-3.5" /> Origem
                </span>
                {[
                  { value: "", label: "Todas" },
                  { value: "manual_import", label: "📥 Import" },
                  { value: "scraper_osm", label: "🗺️ OSM" },
                  { value: "scraper_arcgis", label: "🏛️ ArcGIS" },
                  { value: "manual", label: "✍️ Manual" },
                ].map((opt) => {
                  const active = source === opt.value;
                  return (
                    <button key={opt.value || "all"} type="button"
                      onClick={() => setSource(opt.value)}
                      data-testid={`source-chip-${opt.value || "all"}`}
                      className={"h-7 px-2.5 rounded-full border text-[11px] font-bold transition "
                        + (active
                            ? "bg-indigo-500/25 border-indigo-400/60 text-indigo-100"
                            : "border-white/15 text-slate-300 hover:border-indigo-400/40")}>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Row 0 — Saved Views (Iter178) */}
            {savedViews.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap pb-1 border-b border-white/5"
                   data-testid="saved-views-row">
                <span className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold inline-flex items-center gap-1">
                  <Bookmark className="w-3.5 h-3.5" /> Buscas salvas
                </span>
                {savedViews.map((v) => (
                  <span key={v.id}
                        data-testid={`saved-view-chip-${v.id}`}
                        className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1 rounded-full bg-indigo-500/15 border border-indigo-400/40 text-[11.5px] text-indigo-100 font-bold hover:bg-indigo-500/25 hover:border-indigo-400/70 transition cursor-pointer"
                        onClick={() => applySavedView(v)}
                        title={`Aplicar filtros de ${v.name}`}>
                    <span data-testid={`saved-view-apply-${v.id}`}>{v.name}</span>
                    <button type="button"
                            data-testid={`saved-view-delete-${v.id}`}
                            onClick={(e) => deleteSavedView(v, e)}
                            className="w-4 h-4 rounded-full hover:bg-white/10 flex items-center justify-center"
                            title="Excluir busca salva">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Row 1: full-width search + state + city dropdown + city (Places for edge cases) */}
            <div className="flex flex-col md:flex-row gap-2">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar por nome / endereço"
                  data-testid="prospects-search"
                  className="w-full pl-8 pr-3 h-9 rounded-lg bg-black/30 border border-white/10 text-[13px] focus:border-[#DC2626] outline-none"
                />
              </div>
              {/* Iter182 — State dropdown (populated from /stats/by-state) */}
              <select value={stateFilter}
                      onChange={(e) => { setStateFilter(e.target.value); setCity(""); }}
                      data-testid="prospects-state-select"
                      className="h-9 px-2 rounded-lg bg-black/30 border border-white/10 text-[12.5px] w-full md:w-40 focus:border-[#DC2626] outline-none">
                <option value="">Todos estados</option>
                {stateStats.filter((s) => s.state && s.state !== "??").map((s) => (
                  <option key={s.state} value={s.state}>
                    {s.state} · {s.count.toLocaleString()}{s.hot ? ` (${s.hot}🔥)` : ""}
                  </option>
                ))}
              </select>
              {/* Iter182 — City dropdown (scoped to selected state) */}
              <select value={city}
                      onChange={(e) => setCity(e.target.value)}
                      disabled={cities.length === 0}
                      data-testid="prospects-city-select"
                      className="h-9 px-2 rounded-lg bg-black/30 border border-white/10 text-[12.5px] w-full md:w-56 focus:border-[#DC2626] outline-none disabled:opacity-40">
                <option value="">{stateFilter ? `Todas cidades em ${stateFilter}` : "Todas cidades"}</option>
                {cities.map((c) => (
                  <option key={`${c.city}-${c.state}`} value={c.city}>
                    {c.city}{!stateFilter ? `, ${c.state}` : ""} · {c.count}
                  </option>
                ))}
              </select>
              {/* Free-form Places search kept for cities not yet in the DB */}
              <div className="relative w-full md:w-56">
                <MapPin className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 z-10" />
                <PlacesAutocomplete
                  value={city}
                  onChange={(c, meta) => {
                    setCity(c);
                    if (meta?.state) setStateFilter(meta.state);
                  }}
                  bboxBias={activeStateBbox}
                  placeholder="Ou digite cidade livre…"
                  testid="prospects-city"
                  className="w-full pl-8 pr-3 h-9 rounded-lg bg-black/30 border border-white/10 text-[13px] focus:border-[#DC2626] outline-none"
                />
              </div>
            </div>

            {/* Row 2: segment chips + "+" adder */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="prospects-segment-chips">
              <span className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold">
                {selectedSegments.length === 0 ? "Todos os segmentos" : "Segmentos"}
              </span>
              {selectedSegments.map((sid) => (
                <span key={sid} data-testid={`segment-chip-${sid}`}
                      className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1 rounded-full bg-[#DC2626]/20 border border-[#DC2626]/40 text-[11.5px] text-white font-bold capitalize">
                  {segmentName(sid)}
                  <button type="button" onClick={() => removeSegment(sid)}
                          data-testid={`remove-segment-${sid}`}
                          className="w-4 h-4 rounded-full hover:bg-white/10 flex items-center justify-center">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {addingSegment ? (
                <select
                  autoFocus
                  onChange={(e) => e.target.value && toggleSegment(e.target.value)}
                  onBlur={() => setAddingSegment(false)}
                  data-testid="segment-adder-select"
                  className="h-7 px-2 rounded-full bg-black/30 border border-white/20 text-[11.5px] focus:border-[#DC2626] outline-none">
                  <option value="">— pick a segment —</option>
                  {availableSegments.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              ) : (
                availableSegments.length > 0 && (
                  <button type="button" onClick={() => setAddingSegment(true)}
                          data-testid="add-segment-btn"
                          title="Adicionar mais um segmento"
                          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full border border-dashed border-white/25 text-[11.5px] font-bold text-slate-300 hover:text-white hover:border-white/50">
                    <Plus className="w-3.5 h-3.5" /> Segmento
                  </button>
                )
              )}
            </div>

            {/* Row 3: tier + sqft range (dual slider + inputs) */}
            <div className="flex flex-wrap gap-2 items-center">
              <select value={tier} onChange={(e) => setTier(e.target.value)}
                      data-testid="prospects-tier-filter"
                      className="h-9 px-2 rounded-lg bg-black/30 border border-white/10 text-[12.5px]">
                <option value="">Todos tiers</option>
                <option value="hot">🔥 Hot</option>
                <option value="high">🟢 High</option>
                <option value="qualified">🟡 Qualified</option>
                <option value="nurture">🔵 Nurture</option>
                <option value="low">⚪ Low</option>
              </select>
              <div className="flex items-center gap-2 text-[11.5px] text-slate-400"
                   data-testid="prospects-sqft-range">
                <Building2 className="w-3.5 h-3.5 text-slate-500" />
                <span className="hidden sm:inline font-bold">SqFt</span>
                <input type="number" min={0} step={10000}
                       value={minSqft || ""}
                       onChange={(e) => setMinSqft(Number(e.target.value) || 0)}
                       placeholder="min"
                       data-testid="prospects-min-sqft"
                       className="w-24 h-9 px-2 rounded-lg bg-black/30 border border-white/10 text-[12.5px] focus:border-[#DC2626] outline-none tabular-nums" />
                <span className="text-slate-500">–</span>
                <input type="number" min={0} step={10000}
                       value={maxSqft || ""}
                       onChange={(e) => setMaxSqft(Number(e.target.value) || 0)}
                       placeholder="max"
                       data-testid="prospects-max-sqft"
                       className="w-24 h-9 px-2 rounded-lg bg-black/30 border border-white/10 text-[12.5px] focus:border-[#DC2626] outline-none tabular-nums" />
                {/* Quick presets — pill sliders */}
                <div className="hidden md:flex items-center gap-1 ml-1">
                  {[
                    {label: "10k+", min: 10000, max: 0},
                    {label: "50–200k", min: 50000, max: 200000},
                    {label: "200k+", min: 200000, max: 0},
                    {label: "500k+", min: 500000, max: 0},
                  ].map((p) => {
                    const active = minSqft === p.min && maxSqft === p.max;
                    return (
                      <button key={p.label} type="button"
                        data-testid={`sqft-preset-${p.label}`}
                        onClick={() => { setMinSqft(p.min); setMaxSqft(p.max); }}
                        className={"h-6 px-2 rounded-full border text-[10.5px] font-bold transition "
                                   + (active
                                      ? "bg-[#DC2626] border-[#DC2626] text-white"
                                      : "border-white/15 text-slate-300 hover:border-white/40")}>
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Row 3b — Smart Trigger Search (Iter178) */}
            <div className="flex flex-wrap items-center gap-2"
                 data-testid="prospects-trigger-row">
              <span className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold">
                Triggers
              </span>
              {triggerTypes.slice(0, 8).map((t) => {
                const active = selectedTriggers.includes(t.type);
                return (
                  <button key={t.type} type="button"
                    onClick={() => toggleTrigger(t.type)}
                    data-testid={`trigger-chip-${t.type}`}
                    title={`${t.count} prospects with this trigger`}
                    className={"h-7 px-2.5 rounded-full border text-[11px] font-bold transition inline-flex items-center gap-1 "
                               + (active
                                  ? "bg-amber-500/20 border-amber-500/50 text-amber-300"
                                  : "border-white/15 text-slate-300 hover:border-amber-500/40 hover:text-amber-200")}>
                    {triggerLabel(t.type)}
                    {t.count > 0 && <span className="text-[9.5px] opacity-70">{t.count}</span>}
                  </button>
                );
              })}
              <div className="relative flex-1 min-w-[180px]">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input type="text" value={triggerKw}
                       onChange={(e) => setTriggerKw(e.target.value)}
                       placeholder='Palavra-chave (ex: "cold storage", "49xx", "hiring")'
                       data-testid="prospects-trigger-kw"
                       className="w-full pl-8 pr-3 h-8 rounded-lg bg-black/30 border border-white/10 text-[12px] focus:border-amber-500 outline-none" />
              </div>
            </div>

            {/* Row 3c — Radius / Metropolitan Geo-Search (Iter178) */}
            <div className="flex flex-wrap items-center gap-2"
                 data-testid="prospects-radius-row">
              <span className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold inline-flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" /> Raio
              </span>
              <div className="relative w-full sm:w-64">
                <PlacesAutocomplete
                  value={radiusCity}
                  onChange={(c, meta) => {
                    setRadiusCity(c);
                    if (meta?.lat && meta?.lng) {
                      setRadiusLat(meta.lat);
                      setRadiusLng(meta.lng);
                      if (!radiusMiles) setRadiusMiles(50);
                    }
                  }}
                  placeholder="Cidade/Endereço central"
                  testid="prospects-radius-center"
                  className="w-full pl-3 pr-3 h-8 rounded-lg bg-black/30 border border-white/10 text-[12px] focus:border-[#DC2626] outline-none"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <input type="range" min={5} max={500} step={5}
                       value={radiusMiles || 50}
                       disabled={!radiusLat || !radiusLng}
                       onChange={(e) => setRadiusMiles(Number(e.target.value))}
                       data-testid="prospects-radius-slider"
                       className="w-28 accent-[#DC2626] disabled:opacity-40" />
                <span className="text-[11.5px] tabular-nums text-slate-300 font-bold min-w-[52px]">
                  {radiusMiles ? `${radiusMiles} mi` : "—"}
                </span>
              </div>
              {radiusMiles > 0 && radiusLat && radiusLng && (
                <button type="button"
                  onClick={() => { setRadiusCity(""); setRadiusLat(0); setRadiusLng(0); setRadiusMiles(0); }}
                  data-testid="prospects-clear-radius"
                  className="h-7 px-2 rounded-full border border-white/15 text-[10.5px] font-bold text-slate-300 hover:border-[#DC2626] inline-flex items-center gap-1">
                  <X className="w-3 h-3" /> Limpar raio
                </button>
              )}
            </div>

            {/* Row 3d — Current Provider (Iter183 · multi-select Iter184) */}
            <div className="flex flex-wrap items-center gap-2"
                 data-testid="prospects-provider-row">
              <span className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold inline-flex items-center gap-1">
                <Shield className="w-3.5 h-3.5" /> Provedor Atual
              </span>
              {/* Chip pills — click to toggle in/out */}
              {[
                { value: "self_managed", label: "🏢 Self-Managed", bucket: "self_managed" },
                { value: "unknown",      label: "❓ Unknown",       bucket: "unknown" },
                ...providerStats.filter((p) => p.bucket === "provider").map((p) => ({
                  value: p.provider, label: `🛡️ ${p.provider}`,
                  count: p.count, hot: p.hot, bucket: "provider",
                })),
              ].map((chip) => {
                const active = selectedProviders.includes(chip.value);
                const stat = providerStats.find(
                  (s) => (chip.bucket === "self_managed" && s.bucket === "self_managed")
                      || (chip.bucket === "unknown" && s.bucket === "unknown")
                      || (s.provider === chip.value));
                return (
                  <button key={chip.value} type="button"
                    data-testid={`provider-chip-${chip.value}`}
                    onClick={() =>
                      setSelectedProviders((cur) => cur.includes(chip.value)
                        ? cur.filter((x) => x !== chip.value)
                        : [...cur, chip.value])
                    }
                    title={stat ? `${stat.count} prospects` : undefined}
                    className={"h-7 px-2.5 rounded-full border text-[11px] font-bold transition inline-flex items-center gap-1 "
                      + (active
                          ? "bg-emerald-500/25 border-emerald-400/60 text-emerald-100"
                          : "border-white/15 text-slate-300 hover:border-emerald-500/40 hover:text-emerald-200")}>
                    {chip.label}
                    {stat && <span className="text-[9.5px] opacity-70 tabular-nums">{stat.count}</span>}
                  </button>
                );
              })}
              {selectedProviders.length > 0 && (
                <button type="button" onClick={() => setSelectedProviders([])}
                  data-testid="provider-clear"
                  className="h-7 px-2 rounded-full border border-white/15 text-[10.5px] font-bold text-slate-300 hover:border-[#DC2626] inline-flex items-center gap-1">
                  <X className="w-3 h-3" /> Limpar
                </button>
              )}
            </div>

            {/* Row 4: score + sort + clear + count */}
            <div className="flex flex-wrap gap-2 items-center">
              <select value={minScore} onChange={(e) => setMinScore(Number(e.target.value))}
                      data-testid="prospects-min-score"
                      className="h-9 px-2 rounded-lg bg-black/30 border border-white/10 text-[12.5px]">
                <option value={0}>Score ≥ 0</option>
                <option value={40}>Score ≥ 40</option>
                <option value={60}>Score ≥ 60</option>
                <option value={75}>Score ≥ 75</option>
                <option value={90}>Score ≥ 90 (Hot)</option>
              </select>
              <select value={sort} onChange={(e) => setSort(e.target.value)}
                      data-testid="prospects-sort"
                      className="h-9 px-2 rounded-lg bg-black/30 border border-white/10 text-[12.5px]">
                <option value="opportunity_score">Ordenar: Score</option>
                <option value="acv_est">Ordenar: ACV</option>
                <option value="updated_at">Ordenar: Atualização</option>
              </select>
              {hasFilter && (
                <button type="button" onClick={clearFilters}
                  data-testid="prospects-clear-filters"
                  className="h-9 px-3 rounded-lg text-[12px] text-slate-300 hover:text-white hover:bg-white/5 inline-flex items-center gap-1">
                  <X className="w-3.5 h-3.5" /> Limpar
                </button>
              )}
              {hasFilter && (
                <button type="button" onClick={saveCurrentView}
                  disabled={savingView}
                  data-testid="prospects-save-view"
                  className="h-9 px-3 rounded-lg text-[12px] font-bold text-indigo-200 border border-indigo-400/40 bg-indigo-500/10 hover:bg-indigo-500/20 hover:border-indigo-400/70 inline-flex items-center gap-1.5 disabled:opacity-50">
                  <BookmarkPlus className="w-3.5 h-3.5" />
                  Salvar essa busca
                </button>
              )}
              <div className="ml-auto flex items-center gap-1.5 text-[11.5px] text-slate-400">
                <Filter className="w-3.5 h-3.5" />
                <span data-testid="prospects-count">{items.length} nesta página · {total} total</span>
              </div>
            </div>
            </div>{/* /Iter265 mobile-filters-panel */}
          </div>

          {/* Table (desktop) / Cards (mobile) */}
          {loading ? (
            <div className="h-40 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-[#DC2626]" />
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border p-6 sm:p-8 space-y-4"
                 style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(220,38,38,0.20)" }}
                 data-testid="prospects-empty">
              <div className="text-center text-slate-400 text-[13px]">
                Nenhum prospect encontrado com esses filtros.
              </div>
              {fallbackSuggestions.length > 0 && (
                <div className="border-t border-white/10 pt-4 space-y-2"
                     data-testid="prospects-fallback-suggestions">
                  <div className="text-[10.5px] uppercase tracking-widest text-amber-400 font-bold text-center">
                    💡 Sugestões inteligentes — relaxe um filtro para ver resultados
                  </div>
                  <div className="flex flex-wrap justify-center gap-2 pt-1">
                    {fallbackSuggestions.map((s) => (
                      <button key={s.relax} type="button"
                        onClick={() => applyFallback(s.relax)}
                        data-testid={`fallback-suggestion-${s.relax}`}
                        className="h-9 px-3 rounded-lg border border-amber-500/40 bg-amber-500/10 text-[12px] font-bold text-amber-200 hover:bg-amber-500/20 hover:border-amber-500/70 transition inline-flex items-center gap-2">
                        <span>{s.label}</span>
                        <span className="text-[10.5px] px-1.5 py-0.5 rounded bg-amber-500/25 tabular-nums">
                          {s.count.toLocaleString()} resultados
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Iter181 — Bulk action toolbar */}
              {(selectedIds.size > 0 || bulkRunning || bulkJob) && (
                <div className="rounded-2xl border p-3 flex items-center gap-3 flex-wrap"
                     data-testid="bulk-toolbar"
                     style={{ background: "linear-gradient(135deg,rgba(139,92,246,0.15) 0%,rgba(15,23,42,0.7) 60%)",
                              borderColor: "rgba(139,92,246,0.45)" }}>
                  {selectedIds.size > 0 && !bulkRunning && (
                    <>
                      <span className="text-[12.5px] font-bold text-indigo-200" data-testid="bulk-selection-count">
                        {selectedIds.size} selecionado(s)
                      </span>
                      <button type="button" onClick={runBulkEnrich}
                              disabled={bulkRunning || selectedIds.size === 0}
                              data-testid="bulk-enrich-btn"
                              className="h-9 px-3 rounded-lg font-bold text-[12.5px] text-white inline-flex items-center gap-1.5 disabled:opacity-50"
                              style={{ background: "linear-gradient(135deg,#8B5CF6 0%,#6D28D9 100%)" }}>
                        <Wand2 className="w-3.5 h-3.5" />
                        Enrich Selected
                      </button>
                      <button type="button" onClick={clearSelection}
                              data-testid="bulk-clear-btn"
                              className="h-8 px-2 rounded-lg text-[11.5px] text-slate-300 hover:text-white hover:bg-white/5 inline-flex items-center gap-1">
                        <X className="w-3 h-3" /> Limpar seleção
                      </button>
                    </>
                  )}
                  {(bulkRunning || (bulkJob && bulkJob.status !== "done")) && bulkJob && (
                    <div className="flex-1 flex items-center gap-3" data-testid="bulk-progress">
                      <Loader2 className="w-4 h-4 animate-spin text-indigo-300" />
                      <div className="flex-1 min-w-[180px]">
                        <div className="h-2 bg-black/40 rounded-full overflow-hidden">
                          <div className="h-full bg-indigo-500 transition-all"
                               style={{ width: `${bulkJob.total ? (bulkJob.progress || 0) * 100 / bulkJob.total : 0}%` }} />
                        </div>
                        <div className="text-[10.5px] text-slate-300 mt-1 tabular-nums">
                          {bulkJob.progress || 0} / {bulkJob.total || 0} · rodando enrich...
                        </div>
                      </div>
                    </div>
                  )}
                  {bulkJob && bulkJob.status === "done" && !bulkRunning && (
                    <div className="flex-1 flex items-center justify-between gap-3" data-testid="bulk-done">
                      <div className="text-[12px] text-emerald-300 font-bold">
                        ✓ {bulkJob.ok_count}/{bulkJob.total} OK · +{bulkJob.dms_total} DMs
                      </div>
                      <button type="button" onClick={() => setBulkJob(null)}
                              data-testid="bulk-dismiss"
                              className="h-7 px-2 rounded text-[11px] text-slate-300 hover:text-white hover:bg-white/5">
                        Fechar
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Iter234 · Sweep progress banner (background job) */}
              {sweepJob && sweepJob.job_id && sweepJob.phase !== "done" && (
                <div className="rounded-2xl border p-3 flex items-center gap-3"
                     data-testid="sweep-progress-banner"
                     style={{ background: "linear-gradient(135deg,rgba(14,165,233,0.15) 0%,rgba(15,23,42,0.7) 60%)",
                              borderColor: "rgba(14,165,233,0.45)" }}>
                  <Loader2 className="w-4 h-4 animate-spin text-sky-300" />
                  <div className="flex-1 min-w-[180px]">
                    <div className="h-2 bg-black/40 rounded-full overflow-hidden">
                      <div className="h-full bg-sky-500 transition-all"
                           style={{ width: `${sweepJob.total ? (sweepJob.processed || 0) * 100 / sweepJob.total : 0}%` }} />
                    </div>
                    <div className="text-[10.5px] text-slate-300 mt-1 tabular-nums">
                      Sweep BG: {sweepJob.processed || 0} / {sweepJob.total || 0} ·
                      recuperados <b className="text-emerald-300">{sweepJob.recovered || 0}</b> ·
                      arquivados <b className="text-amber-300">{sweepJob.archived || 0}</b>
                    </div>
                  </div>
                </div>
              )}
              {pagedSweepRunning && pagedSweepStats && (
                <div className="rounded-2xl border p-3 flex items-center gap-3"
                     data-testid="paged-sweep-banner"
                     style={{ background: "linear-gradient(135deg,rgba(139,92,246,0.15) 0%,rgba(15,23,42,0.7) 60%)",
                              borderColor: "rgba(139,92,246,0.45)" }}>
                  <Loader2 className="w-4 h-4 animate-spin text-purple-300" />
                  <div className="text-[12px] text-purple-100 font-bold tabular-nums">
                    Sweep paginado · batch {pagedSweepStats.batches} ·
                    {pagedSweepStats.processed} processados ·
                    <span className="text-emerald-300"> {pagedSweepStats.recovered} recuperados</span>
                  </div>
                </div>
              )}

              {/* Iter234 · Hygiene bar + tier accordion controls */}
              <div className="rounded-2xl border p-3 flex items-center gap-2 flex-wrap"
                   data-testid="prospects-hygiene-bar"
                   style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(220,38,38,0.20)" }}>
                <button type="button"
                  onClick={() => setOnlyWithEmail((v) => !v)}
                  data-testid="prospects-only-email-toggle"
                  title="Hide leads with no valid DM email"
                  className={`h-7 px-2.5 rounded text-[11px] font-bold inline-flex items-center gap-1 border transition-colors ${onlyWithEmail
                    ? "border-emerald-500 bg-emerald-500/15 text-emerald-100"
                    : "border-white/15 text-slate-300 hover:border-emerald-500/40"}`}>
                  <Mail className="w-3 h-3" />
                  {onlyWithEmail ? "Só com e-mail" : "Todos"}
                </button>
                {hiddenNoEmailCount > 0 && (
                  <span className="text-[11px] text-emerald-200"
                        data-testid="prospects-hidden-count">
                    <b>{hiddenNoEmailCount}</b> lead(s) sem e-mail ocultos
                  </span>
                )}
                <div className="h-5 w-px bg-white/10 mx-1" />
                <span className="text-[10.5px] uppercase tracking-widest text-slate-500 font-bold">
                  Layers by tier
                </span>
                <button type="button" onClick={expandAllTiers}
                  data-testid="prospects-tiers-expand-all"
                  className="h-7 px-2 rounded text-[10.5px] font-bold text-slate-300 border border-white/15 hover:border-white/40 inline-flex items-center gap-1">
                  <ChevronDown className="w-3 h-3" /> Expandir tudo
                </button>
                <button type="button" onClick={collapseAllTiers}
                  data-testid="prospects-tiers-collapse-all"
                  className="h-7 px-2 rounded text-[10.5px] font-bold text-slate-300 border border-white/15 hover:border-white/40 inline-flex items-center gap-1">
                  <ChevronUp className="w-3 h-3" /> Colapsar tudo
                </button>
                <div className="ml-auto text-[11px] text-slate-400 tabular-nums"
                     data-testid="prospects-visible-count">
                  {visibleItems.length} visíveis · {total} total
                </div>
              </div>

              {/* Iter234 · Tier accordion — desktop + mobile share one loop */}
              <div className="space-y-2" data-testid="prospects-tier-accordion">
                {tierGroups.map((g) => {
                  const isOpen = expandedTiers.has(g.id);
                  const empty = g.total_count === 0;
                  const chipsVisible = g.total_count >= 2
                    && (g.top_segments.length + g.top_cities.length) > 0;
                  return (
                    <div key={g.id}
                         data-testid={`tier-group-${g.id}`}
                         className="rounded-2xl border overflow-hidden"
                         style={{ background: "rgba(15,23,42,0.7)",
                                    borderColor: (isOpen || g.filter_active)
                                      ? `${g.color}66`
                                      : "rgba(255,255,255,0.08)" }}>
                      <button type="button"
                        onClick={() => !empty && toggleTierExpand(g.id)}
                        disabled={empty}
                        data-testid={`tier-toggle-${g.id}`}
                        aria-expanded={isOpen}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left ${empty ? "opacity-40 cursor-default" : "hover:bg-white/5"}`}>
                        <span className="w-8 h-8 rounded-lg flex items-center justify-center text-[16px]"
                              style={{ background: `${g.color}22`, color: g.color }}>
                          {g.icon}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-extrabold text-white flex items-center gap-2 flex-wrap">
                            {g.label}
                            <span className="text-[10.5px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded"
                                  style={{ background: `${g.color}22`, color: g.color }}
                                  data-testid={`tier-count-${g.id}`}>
                              {g.filter_active
                                ? <><b>{g.count}</b>/{g.total_count}</>
                                : g.count}
                            </span>
                            {g.no_email > 0 && (
                              <span className="text-[9.5px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 uppercase tracking-widest">
                                {g.no_email} sem e-mail
                              </span>
                            )}
                            {g.filter_active && (
                              <span className="text-[9.5px] font-bold px-1.5 py-0.5 rounded bg-indigo-500/25 text-indigo-200 uppercase tracking-widest"
                                    data-testid={`tier-filter-badge-${g.id}`}>
                                Filtro ativo
                              </span>
                            )}
                          </div>
                          <div className="text-[10.5px] text-slate-400 tabular-nums">
                            ACV
                            {g.filter_active
                              ? <>: <b className="text-emerald-300">{usd(g.acv)}</b> de <b className="text-slate-300">{usd(g.total_acv)}</b></>
                              : <> total: <b className="text-emerald-300">{usd(g.acv)}</b></>}
                            {g.count > 0 && (
                              <> · média <b className="text-emerald-200">{usd(g.acv / g.count)}</b></>
                            )}
                          </div>
                        </div>
                        {!empty && (isOpen
                          ? <ChevronUp className="w-4 h-4 text-slate-400" />
                          : <ChevronDown className="w-4 h-4 text-slate-400" />)}
                      </button>

                      {/* Iter236 · Inline filter chips row — shown even when
                          the tier accordion is collapsed so reps can drill
                          straight into "Hot in Miami" without expanding. */}
                      {chipsVisible && (
                        <div className="border-t border-white/10 px-4 py-2 flex items-center gap-2 flex-wrap"
                             data-testid={`tier-filters-${g.id}`}
                             style={{ background: g.filter_active ? `${g.color}0A` : "rgba(0,0,0,0.10)" }}>
                          <Filter className="w-3 h-3 text-slate-500" />
                          {g.top_segments.length > 0 && (
                            <>
                              <span className="text-[9.5px] uppercase tracking-widest text-slate-500 font-bold">Segmento</span>
                              {g.top_segments.map((s) => {
                                const active = g.filter.segment === s.value;
                                return (
                                  <button key={`s-${s.value}`}
                                    type="button"
                                    onClick={() => setTierFilter(g.id, "segment", s.value)}
                                    data-testid={`tier-filter-seg-${g.id}-${s.value}`}
                                    title={`Filtrar ${g.label} por ${s.value.replace(/_/g," ")}`}
                                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded border capitalize transition-colors ${active
                                      ? "border-transparent text-white"
                                      : "border-white/10 text-slate-300 hover:border-white/40"}`}
                                    style={active
                                      ? { background: g.color, borderColor: g.color }
                                      : undefined}>
                                    {s.value.replace(/_/g, " ")} <span className="opacity-60 tabular-nums">{s.count}</span>
                                  </button>
                                );
                              })}
                            </>
                          )}
                          {g.top_cities.length > 0 && (
                            <>
                              <span className="h-3 w-px bg-white/10 mx-0.5" />
                              <span className="text-[9.5px] uppercase tracking-widest text-slate-500 font-bold">Cidade</span>
                              {g.top_cities.map((c) => {
                                const active = g.filter.city === c.value;
                                return (
                                  <button key={`c-${c.value}`}
                                    type="button"
                                    onClick={() => setTierFilter(g.id, "city", c.value)}
                                    data-testid={`tier-filter-city-${g.id}-${c.value}`}
                                    title={`Filtrar ${g.label} em ${c.value}`}
                                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded border transition-colors inline-flex items-center gap-1 ${active
                                      ? "border-transparent text-white"
                                      : "border-white/10 text-slate-300 hover:border-white/40"}`}
                                    style={active
                                      ? { background: g.color, borderColor: g.color }
                                      : undefined}>
                                    <MapPin className="w-2.5 h-2.5" />
                                    {c.value} <span className="opacity-60 tabular-nums">{c.count}</span>
                                  </button>
                                );
                              })}
                            </>
                          )}
                          {g.filter_active && (
                            <button type="button"
                              onClick={() => clearTierFilters(g.id)}
                              data-testid={`tier-filter-clear-${g.id}`}
                              className="ml-auto text-[10px] font-bold text-slate-400 hover:text-white inline-flex items-center gap-1">
                              <X className="w-2.5 h-2.5" /> Limpar
                            </button>
                          )}
                        </div>
                      )}

                      {isOpen && g.count > 0 && (
                        <>
                          {/* Desktop table (within tier) */}
                          <div className="hidden md:block border-t border-white/10">
                            <table className="w-full text-[12.5px]"
                                    data-testid={`tier-table-${g.id}`}>
                              <thead>
                                <tr className="text-slate-400 text-[11px] uppercase tracking-wider">
                                  <th className="px-3 py-2 w-8"></th>
                                  <th className="text-left px-4 py-2 font-bold">Score</th>
                                  <th className="text-left px-2 py-2 font-bold">Empresa</th>
                                  <th className="text-left px-2 py-2 font-bold">Segmento</th>
                                  <th className="text-left px-2 py-2 font-bold">Cidade</th>
                                  <th className="text-right px-2 py-2 font-bold">Funcionários</th>
                                  <th className="text-right px-2 py-2 font-bold">SqFt</th>
                                  <th className="text-left px-2 py-2 font-bold">Provedor</th>
                                  <th className="text-left px-2 py-2 font-bold">E-mail</th>
                                  <th className="text-right px-4 py-2 font-bold">ACV Est.</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                                {g.rows.map((p) => {
                                  const hasEmail = _hasValidEmail(p);
                                  return (
                                    <tr key={p.id}
                                        data-testid={`prospect-row-${p.id}`}
                                        className={"hover:bg-white/5 cursor-pointer "
                                          + (selectedIds.has(p.id) ? "bg-indigo-500/10" : "")}
                                        onClick={() => window.location.assign(`/smith/prospects/${p.id}`)}>
                                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                                        <button type="button"
                                                onClick={(e) => toggleRowSelection(p.id, e)}
                                                data-testid={`bulk-select-${p.id}`}
                                                className="text-slate-400 hover:text-indigo-300">
                                          {selectedIds.has(p.id)
                                            ? <CheckSquare className="w-4 h-4 text-indigo-400" />
                                            : <Square className="w-4 h-4" />}
                                        </button>
                                      </td>
                                      <td className="px-4 py-2">
                                        <div className="inline-flex items-center gap-1.5 tabular-nums font-extrabold"
                                             style={{ color: TIER_COLOR[p.tier?.tier] || "#94A3B8" }}>
                                          <span>{p.tier?.icon}</span>
                                          <span>{p.opportunity_score}</span>
                                        </div>
                                      </td>
                                      <td className="px-2 py-2">
                                        <Link to={`/smith/prospects/${p.id}`}
                                              data-testid={`prospect-link-${p.id}`}
                                              className="font-bold text-white hover:text-[#DC2626]">
                                          {p.company?.name}
                                        </Link>
                                        {p.icp_match?.on_icp && (
                                          <span className="ml-2 text-[9.5px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-bold uppercase tracking-wider">
                                            ICP
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-2 py-2 capitalize text-slate-300">
                                        {(p.segment || "").replace("_", " ")}
                                      </td>
                                      <td className="px-2 py-2 text-slate-300">
                                        {p.property?.city}, {p.property?.state}
                                      </td>
                                      <td className="px-2 py-2 text-right tabular-nums text-slate-300">
                                        {p.company?.employees?.toLocaleString() || "—"}
                                      </td>
                                      <td className="px-2 py-2 text-right tabular-nums text-slate-300">
                                        {p.property?.sqft_building?.toLocaleString() || "—"}
                                      </td>
                                      <td className="px-2 py-2 text-slate-300">
                                        {p.security?.current_provider || <span className="text-slate-500 italic">—</span>}
                                      </td>
                                      <td className="px-2 py-2">
                                        {hasEmail ? (
                                          <span className="text-[10.5px] font-bold text-emerald-300 inline-flex items-center gap-1">
                                            <Mail className="w-3 h-3" /> OK
                                          </span>
                                        ) : (
                                          <span className="text-[10.5px] font-bold text-amber-300 uppercase tracking-widest"
                                                data-testid={`prospect-pending-enrich-${p.id}`}>
                                            pending
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-4 py-2 text-right tabular-nums font-extrabold text-emerald-400">
                                        {usd(p.opportunity?.acv_est)}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>

                          {/* Mobile card list (within tier) */}
                          <div className="md:hidden border-t border-white/10 p-2 space-y-2">
                            {g.rows.map((p) => (
                              <Link key={p.id} to={`/smith/prospects/${p.id}`}
                                    data-testid={`prospect-card-${p.id}`}
                                    className="block rounded-xl border p-3"
                                    style={{ borderColor: "rgba(255,255,255,0.10)" }}>
                                <div className="flex items-center justify-between gap-2 mb-1.5">
                                  <div className="min-w-0">
                                    <div className="text-[13.5px] font-bold text-white truncate">{p.company?.name}</div>
                                    <div className="text-[10.5px] text-slate-400 capitalize truncate">
                                      {(p.segment || "").replace("_", " ")} · {p.property?.city}, {p.property?.state}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 tabular-nums font-extrabold text-[13px] shrink-0"
                                       style={{ color: TIER_COLOR[p.tier?.tier] || "#94A3B8" }}>
                                    <span>{p.tier?.icon}</span>
                                    <span>{p.opportunity_score}</span>
                                  </div>
                                </div>
                                <div className="flex items-center justify-between text-[11px] text-slate-400">
                                  <span>{p.company?.employees?.toLocaleString() || "—"} emp · {p.property?.sqft_building ? `${Math.round(p.property.sqft_building / 1000)}k sqft` : "—"}</span>
                                  <span className="text-emerald-400 font-bold tabular-nums">{usd(p.opportunity?.acv_est)}</span>
                                </div>
                              </Link>
                            ))}
                          </div>
                        </>
                      )}
                      {isOpen && g.count === 0 && g.filter_active && (
                        <div className="border-t border-white/10 p-6 text-center text-slate-400 text-[12px]"
                             data-testid={`tier-filter-empty-${g.id}`}>
                          Nenhum lead em <b className="text-white capitalize">{g.label}</b> corresponde ao filtro.
                          {" "}
                          <button type="button"
                            onClick={() => clearTierFilters(g.id)}
                            className="text-[11.5px] font-bold underline hover:text-white">
                            Limpar filtro
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Pagination — Iter165 · 30 per page */}
          {!loading && totalPages > 1 && (
            <div className="rounded-2xl border p-3 flex items-center justify-between gap-3 flex-wrap"
                 data-testid="prospects-pagination"
                 style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(220,38,38,0.20)" }}>
              <span className="text-[11.5px] text-slate-400">
                Página <span className="font-bold text-white">{page}</span> de{" "}
                <span className="font-bold text-white">{totalPages}</span>
                {" · "}<span className="text-slate-500">{PAGE_SIZE} resultados por página</span>
              </span>
              <div className="flex items-center gap-1.5">
                <button type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  data-testid="pagination-prev"
                  className="h-8 px-3 rounded-lg border border-white/15 text-[12px] font-bold hover:border-[#DC2626] inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
                  <ChevronLeft className="w-3.5 h-3.5" /> Anterior
                </button>
                {/* Compact page number pills — max 5 visible */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((n) => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                  .map((n, idx, arr) => {
                    const prev = arr[idx - 1];
                    return (
                      <span key={n} className="flex items-center gap-1">
                        {prev && n - prev > 1 && <span className="text-slate-500 text-[11px]">…</span>}
                        <button type="button" onClick={() => setPage(n)}
                          data-testid={`pagination-page-${n}`}
                          className={"w-8 h-8 rounded-lg text-[12px] font-bold border transition-colors "
                                     + (n === page
                                        ? "bg-[#DC2626] border-[#DC2626] text-white"
                                        : "border-white/15 text-slate-300 hover:border-white/40")}>
                          {n}
                        </button>
                      </span>
                    );
                  })}
                <button type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  data-testid="pagination-next"
                  className="h-8 px-3 rounded-lg border border-white/15 text-[12px] font-bold hover:border-[#DC2626] inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
                  Próxima <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}


/** Iter249 · Table ↔ Kanban view toggle.
 *  A pill-shaped 2-state toggle rendered at the top of Prospects and
 *  Kanban. Uses `<Link>` so deep-linking + browser history keep working. */
import { Link as _RouterLink } from "react-router-dom";
export function ProspectsViewToggle({ current }) {
  const base = "h-8 px-3 rounded-md text-[11px] font-extrabold uppercase tracking-widest inline-flex items-center gap-1.5";
  const active = "bg-[#DC2626] text-white";
  const idle = "text-slate-400 hover:text-white";
  return (
    <div className="inline-flex items-center gap-1 rounded-lg border border-white/10 bg-black/40 p-1"
         data-testid="prospects-view-toggle">
      <_RouterLink to="/smith/prospects"
                     data-testid="prospects-view-toggle-table"
                     className={`${base} ${current === "table" ? active : idle}`}>
        Table
      </_RouterLink>
      <_RouterLink to="/smith/kanban"
                     data-testid="prospects-view-toggle-kanban"
                     className={`${base} ${current === "kanban" ? active : idle}`}>
        Kanban
      </_RouterLink>
    </div>
  );
}
